<template>
  <div>
    <div v-if="needHeader" class="d-flex align-items-center mb-4">
      <h3 class="mb-0">Клиенты: {{ groupLabel(tableTitle) }}</h3>
      <span v-if="serverSide" class="ml-2 text-secondary">({{ total }})</span>
      <div class="ml-4">
        <b-icon
          v-if="$mq != 'sm'"
          icon="search"
          scale="1.3"
          class="table-input-search_icon"
          variant="secondary"
        ></b-icon>
        <input class="form-control table-input-search" type="search" placeholder="Поиск" v-model="filter">
      </div>

      <b-button
        v-if="rowsCount > 0"
        size="md"
        class="ml-auto"
        :disabled="exportLoading || isExportData"
        @click="exportAll"
      >
        Экспорт
        <b-icon
          v-if="!exportLoading && !isExportData"
          icon="cloud-download"
          aria-hidden="true"
        ></b-icon>
        <i class="fas fa-spinner fa-spin" v-else></i>
      </b-button>
    </div>

    <b-table
      id="client-portfolio-table"
      table-class="v-table"
      primary-key="id"
      hover
      show-empty
      :sticky-header="maxHeight"
      :no-local-sorting="serverSide"
      :sort-by="serverSide ? sortBy : localSortBy"
      :sort-desc="serverSide ? sortDesc : localSortDesc"
      :busy="busy"
      :fields="tableFields"
      :items="serverSide ? tableData : filteredTableData"
      @sort-changed="onSortChanged"
    >
      <template #table-busy>
        <div class="d-flex align-items-center justify-content-center text-secondary py-5">
          <b-spinner small class="mr-2"></b-spinner>
          Загружаем клиентов...
        </div>
      </template>

      <template #cell()="{ value }">
        <div>{{ value }}</div>
      </template>
      <template #cell(name)="data">
        <div class="d-flex align-items-center w-100">
          <span
            class="mr-auto sidebar-link"
            @click="openCompanySidebar(data.item.id)"
          >
            <b>{{ data.value }}</b>
          </span>
          <div class="v-table__buttons">
            <b-icon-umbrella
              v-if="data.item.subcompanies && data.item.subcompanies.length > 0"
              class="cursor-pointer ml-auto"
              variant="secondary"
              font-scale="1.2"
              @click="openSubcompanies(data.item)"
            ></b-icon-umbrella>

            <b-overlay
              :show="data.item.loading"
              rounded
              opacity="0.6"
              spinner-small
              spinner-variant="primary"
            >
              <span
                v-if="data.item.to_sales && !data.item.has_sales_task"
                v-b-tooltip.hover
                title="Отправить в отдел продаж"
                class="cursor-pointer"
                @click="sendTicketToSales(data.item)"
              >
                <b-icon-arrow-down-right-square
                  font-scale="1.4"
                  variant="success"
                />
              </span>
              <b-icon-check
                v-if="data.item.to_sales && data.item.has_sales_task"
                font-scale="1.4"
                variant="success"
              />
            </b-overlay>

            <b-overlay
              v-if="canToggleRisk(data.item)"
              :show="data.item.loading_risk"
              rounded
              opacity="0.6"
              spinner-small
              spinner-variant="primary"
            >
              <span
                v-b-tooltip.hover
                :title="data.item.is_at_risk ? 'Снять признак «В зоне риска»' : 'Отметить «В зоне риска»'"
                class="cursor-pointer"
                @click="toggleRisk(data.item)"
              >
                <b-icon-exclamation-triangle-fill
                  v-if="data.item.is_at_risk"
                  font-scale="1.3"
                  variant="danger"
                />
                <b-icon-exclamation-triangle
                  v-else
                  font-scale="1.3"
                  variant="secondary"
                />
              </span>
            </b-overlay>
          </div>
        </div>
      </template>

      <template #cell(status_name)="data">
        <div>
          {{ data.value }}
          <b-badge v-if="data.item.is_active" variant="success" class="ml-1">Активный</b-badge>
        </div>
      </template>

      <template #cell(is_at_risk)="data">
        <div class="text-center">
          <b-badge v-if="data.item.is_at_risk" variant="danger">Риск</b-badge>
          <span v-else>-</span>
        </div>
      </template>

      <template #cell(last_comment)="data">
        <template v-if="data.item.last_comment">
          <div class="company-last-comment pb-0"><span v-html="data.item.last_comment.comment"></span></div>
          <div class="text-right h5-small-text text-secondary pt-0 pb-2">{{ data.item.last_comment.created_at }}</div>
        </template>
        <template v-else>
          <div>-</div>
        </template>
      </template>

      <template v-for="period in periods" #[`cell(${period})`]="data">
        <div
          v-if="data.value > 0"
          :key="period"
          @click="showByIds(data.item[data.field.key], data.item.name)"
          class="cursor-pointer text-center"
        >
          <span class="underline-dotted">
            {{ data.value }}
          </span>
        </div>
        <div class="text-center" :key="period" v-else>
          {{ data.value }}
        </div>
      </template>

      <template #empty>
        <div class="d-flex flex-column align-items-center justify-content-center">
          <template v-if="error">
            <div class="mb-2">Не удалось загрузить клиентов</div>
            <b-button size="sm" variant="outline-primary" @click="$emit('retry')">Повторить</b-button>
          </template>
          <template v-else>Нет данных</template>
        </div>
      </template>
    </b-table>

    <div v-if="serverSide && total > 0" class="portfolio-pagination">
      <div class="text-secondary">
        Показано {{ shownFrom }}-{{ shownTo }} из {{ total }}
      </div>
      <b-pagination
        v-if="total > perPage"
        :value="page"
        :total-rows="total"
        :per-page="perPage"
        :disabled="busy"
        size="sm"
        limit="7"
        class="mb-0"
        @change="$emit('page-change', $event)"
      />
      <div class="d-flex align-items-center">
        <span class="text-secondary mr-2">Строк:</span>
        <b-form-select
          :value="perPage"
          :options="perPageOptions"
          :disabled="busy"
          size="sm"
          @change="$emit('per-page-change', $event)"
        />
      </div>
    </div>

    <!-- Плоская таблица только на время экспорта: вся группа, без тяжёлых ячеек b-table -->
    <table v-if="exportRows.length > 0" id="client-portfolio-export" class="d-none">
      <thead>
        <tr>
          <th v-for="field in exportFields" :key="field.key">{{ field.label }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in exportRows" :key="row.id">
          <td
            v-for="field in exportFields"
            :key="field.key"
            :data-t="field.tdAttr && field.tdAttr['data-t'] === 'n' ? 'n' : null"
          >{{ exportValue(row, field) }}</td>
        </tr>
      </tbody>
    </table>

    <OrderList
      v-if="orderList && orderList.length > 0"
      :openOrderList="openOrderList"
      :orderList="orderList"
      :label="orderListLabel"
      @closeOrderList="closeOrderList"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { excelExportHelpers } from "../../mixins";
import { openCompanySidebar } from '../../mixins/openCompanySidebar';
import debounce from '../../services/helpers';
import { groupLabel } from "./portfolioGroups";

import OrderList from "@components/orders/OrderList";
import { API_TO_SALES_CLIENTS_PORTFOLIO, API_COMPANY } from "@/constants";

const PERIODS = ["1_month", "3_month", "6_month", "1_year", "all"];

export default {
  components: {
    OrderList,
  },

  mixins: [
    excelExportHelpers,
    openCompanySidebar
  ],

  props: {
    tableData: {
      type: Array,
      required: true,
    },

    tableFields: {
      type: Array,
      required: true,
    },

    needHeader: {
      type: Boolean,
      default: true,
    },

    maxHeight: {
      type: String,
      default: "600px",
    },

    // Серверный режим (основная таблица портфеля): сортировка, поиск и страницы - на бэкенде.
    // Без него (модальное окно связанных компаний) всё считается локально.
    serverSide: {
      type: Boolean,
      default: false,
    },

    busy: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 50 },
    sortBy: { type: String, default: "" },
    sortDesc: { type: Boolean, default: false },
    search: { type: String, default: "" },
  },

  data() {
    return {
      periods: PERIODS,
      perPageOptions: [25, 50, 100, 200],
      filteredTableData: [],
      orderList: [],
      openOrderList: false,
      orderListLabel: "",
      localSortBy: "id",
      localSortDesc: true,
      filter: this.search,
      exportRows: [],
      exportLoading: false,
    };
  },

  computed: {
    ...mapGetters("clientPortfolio", ["tableTitle"]),

    rowsCount() {
      return this.serverSide ? this.total : this.filteredTableData.length;
    },
    shownFrom() {
      return (this.page - 1) * this.perPage + 1;
    },
    shownTo() {
      return Math.min(this.page * this.perPage, this.total);
    },
    exportName() {
      return `Клиентский портфель "${groupLabel(this.tableTitle)}"`;
    },
    exportFields() {
      return this.tableFields.filter((field) => field.key !== "id");
    },
  },

  watch: {
    filter: debounce(function (value) {
      if (this.serverSide) {
        if (value !== this.search) {
          this.$emit("search", value);
        }
      } else {
        this.filterData(value);
      }
    }, 500),
    // Поиск сбрасывается при смене группы
    search(value) {
      this.filter = value;
    },
    tableData() {
      if (!this.serverSide) {
        this.filterData(this.filter || "");
      }
    },
    isExportData(value) {
      if (!value) {
        this.exportRows = [];
      }
    },
  },

  created() {
    if (!this.serverSide) {
      this.filterData("");
    }
  },

  methods: {
    ...mapActions("clientPortfolio", ["loadAllRows", "riskChanged"]),
    groupLabel,

    onSortChanged(ctx) {
      if (this.serverSide) {
        this.$emit("sort-changed", { sortBy: ctx.sortBy, sortDesc: ctx.sortDesc });
      } else {
        this.localSortBy = ctx.sortBy;
        this.localSortDesc = ctx.sortDesc;
      }
    },

    openSubcompanies(item) {
      this.$store.commit("clientPortfolio/SET_SUBCOMPANIES", item);
    },

    showByIds(orders, company) {
      this.orderList = orders;
      this.orderListLabel = `Заказы ${company}`;
      this.openOrderList = true;
    },

    closeOrderList() {
      this.openOrderList = false;
      this.orderListLabel = "";
      this.orderList = [];
    },

    filterData(searchLine) {
      const preparedFilter = searchLine.toLowerCase();
      this.filteredTableData = this.tableData.filter((element) => {
        return [element.name, element.inn, element.sales_manager_name, element.to_manager_name]
          .some((value) => (value || "").toString().toLowerCase().includes(preparedFilter));
      });
    },

    async exportAll() {
      if (!this.serverSide) {
        this.exportRows = this.filteredTableData;
      } else {
        this.exportLoading = true;
        try {
          this.exportRows = await this.loadAllRows();
        } catch (e) {
          this.$toast.error("Не удалось выгрузить клиентов");
          return;
        } finally {
          this.exportLoading = false;
        }
      }

      await this.$nextTick();
      this.exportTable("client-portfolio-export", this.exportName);
      // Нет прав на экспорт - exportTable показал сообщение и не начал выгрузку
      if (!this.isExportData) {
        this.exportRows = [];
      }
    },

    exportValue(row, field) {
      const value = row[field.key];
      if (field.key === "last_comment") {
        if (!value || !value.comment) {
          return "";
        }
        const div = document.createElement("div");
        div.innerHTML = value.comment;
        return (div.textContent || "").trim();
      }
      if (field.key === "is_at_risk") {
        return value ? "Да" : "";
      }
      if (field.formatter) {
        return field.formatter(value, field.key, row);
      }
      return value === null || value === undefined ? "" : value;
    },

    sendTicketToSales(item) {
      item.loading = true;
      axios
        .get(`${API_TO_SALES_CLIENTS_PORTFOLIO}/${item.id}`)
        .then(({ data }) => {
          this.$toast.success("Запрос успешно отправлен");
          item.has_sales_task = data.has_sales_ticket;
        })
        .finally(() => {
          item.loading = false;
        });
    },

    // «В зоне риска» можно ставить только неархивным клиентам
    canToggleRisk(item) {
      return this.serverSide
        && item.status_code !== undefined
        && item.status_code !== null
        && !["black_list", "liquidated"].includes(item.status_code);
    },

    toggleRisk(item) {
      const isAtRisk = !item.is_at_risk;
      item.loading_risk = true;

      axios
        .put(`${API_COMPANY}/${item.id}/risk`, { is_at_risk: isAtRisk })
        .then(({ data }) => {
          this.riskChanged({ id: item.id, is_at_risk: data.is_at_risk });
          this.$toast.success(data.is_at_risk ? "Клиент отмечен «В зоне риска»" : "Признак «В зоне риска» снят");
        })
        .finally(() => {
          item.loading_risk = false;
        });
    },
  },
};
</script>

<style lang="scss">
.v-table {
  th {
    padding: 0 !important;
    border-top: none;

    > div {
      font-size: .8rem;
      padding: 10px 20px;
      font-weight: bold;
      position: relative;
      white-space: nowrap;
      color: #000;
      &::after {
        content: "";
        width: 100%;
        height: 100%;
        background-color: #b8b8b836;
        position: absolute;
        z-index: -1;
        top: 0;
        left: 0;
      }
    }
  }

  td {
    padding: 0 !important;
    > div {
      padding: 20px;
    }
  }

  &__buttons {
    display: flex;
    align-items: center;
    > * {
      margin-left: 10px;
    }
  }
}

.b-table-sticky-header {
  margin-bottom: 0 !important;
}

.portfolio-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 15px;
}

.underline-dotted {
  border-bottom: 1px dashed #999999;
}
.cursor-pointer:hover .underline-dotted,
.underline-dotted:hover {
  color: var(--orange);
  border-bottom: 1px dashed var(--orange);
}
</style>
